import type { CostAnalyticsResponse, CostAnalyticsState } from "../types/analytics.types";
import type { WeeklyCustomerCostRequest, WeeklyCostReportResponse, WeeklyCostRequest } from "../types/weekly-cost-report.types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";

import { costService } from "../services/costService";
import {
  buildAnalyticsPayloadKey,
} from "../utils/shared/costRequestKey.utils";
import { getErrorMessage, isAbortError } from "../utils/shared/error.utils";
import type { CostAnalyticsPayload } from "../types/analytics.types";

/**
 * State for cost analytics comparison data
 */
const initialState: CostAnalyticsState = {
  analyticsData: null,
  analyticsLoading: false,
  analyticsError: null,
  //Weekly Report
  weeklyReportData: null,
  weeklyReportLoading: false,
  weeklyReportError: null,
  lastWeeklyPayload: null,
  weeklyCustomerReportData: null,
  weeklyCustomerReportLoading: false,
  weeklyCustomerReportError: null,
  lastWeeklyCustomerPayload: null,
};

// Abort controller
let costAnalyticsAbortController: AbortController | null = null;
let lastAnalyticsPayloadKey: string | null = null;
let activeAnalyticsRequestId: string | null = null;

/* ===================== ANALYTICS ===================== */

/**
 * Fetches cost analytics comparison data for current vs previous period.
 * Cancels any in-flight request before starting new one.
 * Skips duplicate requests based on payload comparison.
 * @param payload - Analytics request with time ranges and filters
 * @returns Analytics response with KPI and comparison data
 */
export const fetchCostAnalytics = createAsyncThunk<
  CostAnalyticsResponse,
  CostAnalyticsPayload,
  { state: RootState; rejectValue: string }
>(
  "costAnalytics/fetchCostAnalytics",

  async (payload, { rejectWithValue }) => {
    try {
      // cancel previous request
      if (costAnalyticsAbortController) {
        costAnalyticsAbortController.abort();
      }

      costAnalyticsAbortController = new AbortController();

      return await costService.GetCostAnalyticsSummary(payload, {
        signal: costAnalyticsAbortController.signal,
      });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return rejectWithValue("aborted");
      }

      return rejectWithValue(getErrorMessage(err, "Failed to load analytics"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState();
      // Always fetch if no data (handles page refresh where Redux is reset but module variables persist)
      if (state.costAnalytics.analyticsData === null) {
        lastAnalyticsPayloadKey = null;
        return true;
      }
      const key = buildAnalyticsPayloadKey(payload);
      return key !== lastAnalyticsPayloadKey;
    },
  },
);
/* ===================== Weekly Cost Report ===================== */
export const fetchWeeklyCostReport = createAsyncThunk<
  WeeklyCostReportResponse,
  WeeklyCostRequest,
  { state: RootState; rejectValue: string }
>(
  "costAnalytics/fetchWeeklyCostReport",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.fetchWeeklyCostReport(payload);
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to load analytics"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState();
      const payloadKey = JSON.stringify(payload);

      if (
        state.costAnalytics.weeklyReportLoading &&
        state.costAnalytics.lastWeeklyPayload === payloadKey
      ) {
        return false;
      }

      if (
        state.costAnalytics.weeklyReportData !== null &&
        state.costAnalytics.lastWeeklyPayload === payloadKey
      ) {
        return false;
      }

      return true;
    },
  },
);

export const fetchWeeklyCustomerReport = createAsyncThunk<
  WeeklyCostReportResponse,
  WeeklyCustomerCostRequest,
  { state: RootState; rejectValue: string }
>(
  "costAnalytics/fetchWeeklyCustomerReport",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.fetchWeeklyCustomerReport(payload);
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to load analytics"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState() as RootState;
      const payloadKey = JSON.stringify(payload);

      if (
        state.costAnalytics.weeklyCustomerReportLoading &&
        state.costAnalytics.lastWeeklyCustomerPayload === payloadKey
      ) {
        return false;
      }

      if (
        state.costAnalytics.weeklyCustomerReportData !== null &&
        state.costAnalytics.lastWeeklyCustomerPayload === payloadKey
      ) {
        return false;
      }

      return true;
    },
  },
);

/* ===================== SLICE ===================== */

const costAnalyticsSlice = createSlice({
  name: "costAnalytics",

  initialState,

  reducers: {},

  extraReducers: (builder) => {
    builder

      /* ================= ANALYTICS ================= */

      .addCase(fetchCostAnalytics.pending, (state, action) => {
        activeAnalyticsRequestId = action.meta.requestId;
        state.analyticsLoading = true;
        state.analyticsError = null;
      })

      .addCase(fetchCostAnalytics.fulfilled, (state, action) => {
        if (action.meta.requestId !== activeAnalyticsRequestId) {
          return;
        }

        activeAnalyticsRequestId = null;
        state.analyticsLoading = false;
        state.analyticsData = action.payload;
        lastAnalyticsPayloadKey = buildAnalyticsPayloadKey(action.meta.arg);
      })

      .addCase(fetchCostAnalytics.rejected, (state, action) => {
        if (action.meta.requestId !== activeAnalyticsRequestId) {
          return;
        }
        // Only the latest in-flight request is allowed to own loading state.
        activeAnalyticsRequestId = null;
        state.analyticsLoading = false;
        if (action.payload === "aborted") {
          return;
        }
        state.analyticsError =
          action.payload || action.error.message || "Failed to load analytics";
      })
      // ================= WEEKLY REPORT =================
      .addCase(fetchWeeklyCostReport.pending, (state, action) => {
        state.weeklyReportLoading = true;
        state.weeklyReportError = null;
        state.lastWeeklyPayload = JSON.stringify(action.meta.arg);
      })
      .addCase(fetchWeeklyCostReport.fulfilled, (state, action) => {
        state.weeklyReportLoading = false;
        state.weeklyReportData = action.payload;
      })
      .addCase(fetchWeeklyCostReport.rejected, (state, action) => {
        state.weeklyReportLoading = false;
        state.weeklyReportError =
          action.payload ||
          action.error.message ||
          "Failed to load weekly report";
      })
      .addCase(fetchWeeklyCustomerReport.pending, (state, action) => {
        state.weeklyCustomerReportLoading = true;
        state.weeklyCustomerReportError = null;
        state.lastWeeklyCustomerPayload = JSON.stringify(action.meta.arg);
      })
      .addCase(fetchWeeklyCustomerReport.fulfilled, (state, action) => {
        state.weeklyCustomerReportLoading = false;
        state.weeklyCustomerReportData = action.payload;
      })
      .addCase(fetchWeeklyCustomerReport.rejected, (state, action) => {
        state.weeklyCustomerReportLoading = false;
        state.weeklyCustomerReportError =
          action.payload ||
          action.error.message ||
          "Failed to load weekly customer report";
      });
  },
});

export const selectWeeklyCostReportData = (state: RootState) =>
  state.costAnalytics.weeklyReportData;

export const selectWeeklyCustomerReportData = (state: RootState) =>
  state.costAnalytics.weeklyCustomerReportData;

export default costAnalyticsSlice.reducer;
